import { useState, useEffect } from 'react';
import { getMenus, createMenu, deleteMenu, getStaff, getItems } from '../api';
import MenuDetail from './MenuDetail';
import OpeningHours from './OpeningHours';
import RestaurantProfile from './RestaurantProfile';
import Settings from './Settings';
import Staff from './Staff';


const s = {
  page: { minHeight: '100vh', backgroundColor: '#f5f5f3', fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif' },

  topNav: { backgroundColor: '#fff', borderBottom: '0.5px solid #e8e8e8', padding: '0 32px', height: '56px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  navLeft: { display: 'flex', alignItems: 'center', gap: '32px' },
  navBrand: { fontSize: '15px', fontWeight: '700', color: '#1a1a1a', letterSpacing: '-0.3px' },
  navRight: { display: 'flex', alignItems: 'center', gap: '20px' },
  navLink: { fontSize: '13px', color: '#666', textDecoration: 'none', background: 'none', border: 'none', cursor: 'pointer', padding: 0 },

  subNav: { backgroundColor: '#fff', borderBottom: '0.5px solid #e8e8e8', padding: '0 32px', display: 'flex', gap: '0' },
  subNavBtn: { padding: '12px 16px', border: 'none', borderBottom: '2px solid transparent', backgroundColor: 'transparent', fontSize: '13px', color: '#aaa', cursor: 'pointer', fontWeight: '500' },
  subNavBtnActive: { color: '#1a1a1a', borderBottomColor: '#1a1a1a' },

  content: { maxWidth: '900px', margin: '0 auto', padding: '32px 24px' },
  error: { backgroundColor: '#fdecea', color: '#c0392b', padding: '10px 14px', borderRadius: '6px', marginBottom: '20px', fontSize: '13px' },

  metrics: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '24px' },
  metricCard: { backgroundColor: '#fff', border: '0.5px solid #e8e8e8', borderRadius: '8px', padding: '20px', textAlign: 'center' },
  metricNum: { fontSize: '32px', fontWeight: '700', color: '#1a1a1a', letterSpacing: '-1px', marginBottom: '4px' },
  metricLabel: { fontSize: '11px', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.8px' },

  card: { backgroundColor: '#fff', border: '0.5px solid #e8e8e8', borderRadius: '8px', padding: '24px', marginBottom: '20px' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
  cardTitle: { fontSize: '15px', fontWeight: '600', color: '#1a1a1a' },

  createRow: { display: 'flex', gap: '10px', marginBottom: '20px' },
  input: { flex: 1, padding: '9px 12px', border: '0.5px solid #e0e0e0', borderRadius: '6px', fontSize: '13px', outline: 'none', color: '#1a1a1a' },

  btnPrimary: { padding: '9px 18px', backgroundColor: '#1a1a1a', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '500', cursor: 'pointer', whiteSpace: 'nowrap' },
  btnSecondary: { padding: '7px 14px', backgroundColor: '#fff', color: '#444', border: '0.5px solid #e0e0e0', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', textDecoration: 'none', display: 'inline-block' },

  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th: { padding: '10px 12px', textAlign: 'left', fontSize: '11px', fontWeight: '600', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '0.5px solid #e8e8e8' },
  td: { padding: '14px 12px', color: '#1a1a1a', borderBottom: '0.5px solid #f0f0f0' },
  tdActions: { padding: '14px 12px', borderBottom: '0.5px solid #f0f0f0', display: 'flex', gap: '6px', alignItems: 'center' },

  badgeActive: { fontSize: '11px', backgroundColor: '#f0faf4', color: '#2e7d32', padding: '3px 10px', borderRadius: '20px', fontWeight: '500', border: '0.5px solid #c8e6c9' },
  badgeInactive: { fontSize: '11px', backgroundColor: '#f5f5f5', color: '#999', padding: '3px 10px', borderRadius: '20px', fontWeight: '500', border: '0.5px solid #e0e0e0' },

  actionBtn: { padding: '5px 12px', backgroundColor: '#fff', color: '#444', border: '0.5px solid #e0e0e0', borderRadius: '5px', fontSize: '12px', cursor: 'pointer' },
  actionBtnDanger: { padding: '5px 12px', backgroundColor: '#fff', color: '#c0392b', border: '0.5px solid #f5c6c6', borderRadius: '5px', fontSize: '12px', cursor: 'pointer' },

  muted: { fontSize: '13px', color: '#aaa' },

  qrRow: { display: 'flex', gap: '20px', alignItems: 'flex-start' },
  qrImg: { width: '120px', height: '120px', flexShrink: 0 },
  qrInfo: { flex: 1 },
  qrLabel: { fontSize: '12px', color: '#aaa', marginBottom: '4px' },
  qrUrl: { fontSize: '12px', color: '#666', wordBreak: 'break-all', marginBottom: '12px', lineHeight: 1.5 },
  qrActions: { display: 'flex', gap: '8px' },
};

function Dashboard() {
  const restaurant = JSON.parse(localStorage.getItem('restaurant') || '{}');
  const [menus, setMenus] = useState([]);
  const [newMenuName, setNewMenuName] = useState('');
  const [selectedMenu, setSelectedMenu] = useState(null);

  const [activeTab, setActiveTab] = useState('menus');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [qrData, setQrData] = useState(null);
  const [staffCount, setStaffCount] = useState(0);
  const [itemCount, setItemCount] = useState(0);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const res = await getMenus();
      setMenus(res.data);
      const staffRes = await getStaff();
      setStaffCount(staffRes.data.length);
      if (res.data.length > 0) {
        const itemRes = await getItems(res.data[0].id);
        setItemCount(itemRes.data.length);
      }
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMenu = async (e) => {
    e.preventDefault();
    if (!newMenuName.trim()) return;
    try {
      const res = await createMenu({ name: newMenuName });
      setMenus([...menus, res.data]);
      setNewMenuName('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create menu');
    }
  };

  const handleDeleteMenu = async (id) => {
    if (!window.confirm('Delete this menu?')) return;
    try {
      await deleteMenu(id);
      setMenus(menus.filter(m => m.id !== id));
      if (selectedMenu?.id === id) setSelectedMenu(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete menu');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('restaurant');
    window.location.href = '/';
  };

  const handleShowQR = async (menuId) => {
    try {
      const id = menuId ? parseInt(menuId) : null;
      const res = await fetch((process.env.REACT_APP_API_URL || '') + '/api/public/qr/' + restaurant.slug + (id ? '?menu=' + id : ''));
      const data = await res.json();
      setQrData(data);
    } catch (err) {
      setError('Failed to generate QR code');
    }
  };

  if (selectedMenu) return <MenuDetail menu={selectedMenu} onBack={() => setSelectedMenu(null)} />;
  if (activeTab === 'hours') return <OpeningHours onBack={() => setActiveTab('menus')} onNavigate={setActiveTab} />;
  if (activeTab === 'profile') return <RestaurantProfile onBack={() => setActiveTab('menus')} onNavigate={setActiveTab} />;
  if (activeTab === 'settings') return <Settings onBack={() => setActiveTab('menus')} onNavigate={setActiveTab} />;
  if (activeTab === 'staff') return <Staff onBack={() => setActiveTab('menus')} onNavigate={setActiveTab} />;

  return (
    <div style={s.page}>

      {/* Top nav */}
      <div style={s.topNav}>
        <div style={s.navLeft}>
          <span style={s.navBrand}>{restaurant.name || 'Menu Board'}</span>
        </div>
        <div style={s.navRight}>
          <a href={'/menu/' + restaurant.slug} target="_blank" rel="noreferrer" style={s.navLink}>View menu</a>
          <button style={s.navLink} onClick={handleLogout}>Sign out</button>
        </div>
      </div>

      {/* Sub nav */}
      <div style={s.subNav}>
        {['menus', 'hours', 'profile', 'staff', 'settings'].map(tab => (
          <button
            key={tab}
            style={{ ...s.subNavBtn, ...(activeTab === tab ? s.subNavBtnActive : {}) }}
            onClick={() => setActiveTab(tab)}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      <div style={s.content}>
        {error && <p style={s.error}>{error}</p>}

        {/* Metric cards */}
        <div style={s.metrics}>
          <div style={s.metricCard}>
            <p style={s.metricNum}>{menus.length}</p>
            <p style={s.metricLabel}>Menus</p>
          </div>
          <div style={s.metricCard}>
            <p style={s.metricNum}>{itemCount}</p>
            <p style={s.metricLabel}>Items</p>
          </div>
          <div style={s.metricCard}>
            <p style={s.metricNum}>{staffCount}</p>
            <p style={s.metricLabel}>Staff</p>
          </div>
          <div style={s.metricCard}>
            <p style={s.metricNum}>{menus.filter(m => m.is_active).length}</p>
            <p style={s.metricLabel}>Active menus</p>
          </div>
        </div>

        {/* QR section */}
        <div style={s.card}>
          <div style={s.cardHeader}>
            <h2 style={s.cardTitle}>Menu QR Code</h2>
            <button style={s.btnPrimary} onClick={() => handleShowQR(null)}>Generate QR</button>
          </div>
          {qrData && (
            <div style={s.qrRow}>
              <img src={qrData.qr} alt="QR Code" style={s.qrImg} />
              <div style={s.qrInfo}>
                <p style={s.qrUrl}>{qrData.url}</p>
                <div style={s.qrActions}>
                  <button style={s.btnSecondary} onClick={() => navigator.clipboard.writeText(qrData.url)}>Copy link</button>
                  <a href={qrData.qr} download="menu-qr-code.png" style={s.btnSecondary}>Download QR</a>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Menus */}
        <div style={s.card}>
          <div style={s.cardHeader}>
            <h2 style={s.cardTitle}>Your Menus</h2>
          </div>

          <form onSubmit={handleCreateMenu} style={s.createRow}>
            <input
              style={s.input}
              type="text"
              placeholder="New menu name e.g. Lunch Menu"
              value={newMenuName}
              onChange={(e) => setNewMenuName(e.target.value)}
            />
            <button style={s.btnPrimary} type="submit">Create</button>
          </form>

          {loading ? (
            <p style={s.muted}>Loading...</p>
          ) : menus.length === 0 ? (
            <p style={s.muted}>No menus yet. Create your first one above.</p>
          ) : (
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Name</th>
                  <th style={s.th}>Status</th>
                  <th style={s.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {menus.map((menu, i) => (
                  <tr key={menu.id} style={{ backgroundColor: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={s.td}>{menu.name}</td>
                    <td style={s.td}>
                      <span style={menu.is_active ? s.badgeActive : s.badgeInactive}>
                        {menu.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={s.tdActions}>
                      <button style={s.actionBtn} onClick={() => setSelectedMenu(menu)}>Manage</button>
                      <button style={s.actionBtn} onClick={() => handleShowQR(menu.id)}>QR Code</button>
                      <button style={s.actionBtnDanger} onClick={() => handleDeleteMenu(menu.id)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* QR per menu result */}
        {qrData && qrData.url.includes('menu=') && (
          <div style={s.card}>
            <div style={s.qrRow}>
              <img src={qrData.qr} alt="QR Code" style={s.qrImg} />
              <div style={s.qrInfo}>
                <p style={s.qrLabel}>Menu-specific QR code</p>
                <p style={s.qrUrl}>{qrData.url}</p>
                <div style={s.qrActions}>
                  <button style={s.btnSecondary} onClick={() => navigator.clipboard.writeText(qrData.url)}>Copy link</button>
                  <a href={qrData.qr} download="menu-qr-code.png" style={s.btnSecondary}>Download QR</a>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


export default Dashboard;
